import React from 'react';
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Button,
  Heading,
  Badge,
  Image,
} from '@chakra-ui/react';
import CalendlyButton from '../utils/CalendlyButton';
import UnderlineCTA from '../utils/UnderlineCTA.jsx';
import iconPAC from '../utils/images/iconPAC.png';
import iconIsolation from '../utils/images/iconIsolation.png';
import iconPanneaux from '../utils/images/iconPanneaux.png';
import iconChauffeEau from '../utils/images/iconChauffeEau.png';
import iconVentilation from '../utils/images/iconVentilation.png';
import installation from '../utils/images/installation.png';

const servicesData = [
  {
    title: 'Pompe à chaleur',
    description: 'Réduisez vos factures de chauffage jusqu\'à 70% avec une pompe à chaleur performante.',
    features: ['Économies jusqu\'à 70%', 'Aides MaPrimeRénov\'', 'Installation certifiée RGE'],
    price: 'Aides jusqu\'à 70%',
    icon: iconPAC,
  },
  {
    title: 'Isolation thermique',
    description: 'Isolez vos combles, murs et sols pour un confort optimal toute l\'année.',
    features: ['Combles perdus ou aménagés', 'Isolation des murs', 'Réduction des pertes de chaleur'],
    price: 'À partir de 0€',
    icon: iconIsolation,
  },
  {
    title: 'Panneaux solaires',
    description: 'Produisez votre propre électricité et revendez le surplus à EDF.',
    features: ['Autoconsommation', 'Revente du surplus', 'Garantie 25 ans'],
    price: 'Aides jusqu\'à 40%',
    icon: iconPanneaux,
  },
  {
    title: 'Chauffe-eau solaire',
    description: 'Profitez de l\'énergie solaire pour votre eau chaude sanitaire.',
    features: ['Économies sur l\'eau chaude', 'Écologique', 'Subventions disponibles'],
    price: 'Aides jusqu\'à 50%',
    icon: iconChauffeEau,
  },
  {
    title: 'Ventilation VMC',
    description: 'Améliorez la qualité de l\'air intérieur et réduisez l\'humidité.',
    features: ['VMC double flux', 'Récupération de chaleur', 'Air sain'],
    price: 'Aides MaPrimeRénov\'',
    icon: iconVentilation,
  },
];

const ServicesSection = () => {
  return (
    <Box as="section" pt={0} pb={{ base: 14, lg: 20 }} bg="white" id="services">
      <Container maxW="1200px" px={5}>

        {/* Bande intro – vidéo à gauche, texte à droite (style iOS) */}
        <Box
          as="section"
          position="relative"
          left="50%"
          ml="-50vw"
          w="100vw"
          bg="linear-gradient(135deg, #094c26, #0d6b35, #063a1d)"
          py={{ base: 12, lg: 16 }}
          mb={{ base: 12, lg: 16 }}
        >
          <Container maxW="1200px" px={5}>
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={{ base: 8, lg: 16 }} alignItems="center">
              <GridItem>
                <Box
                  borderRadius="3xl"
                  w="100%"
                  h="100%"
                  overflow="hidden"
                  boxShadow="0 25px 80px rgba(0,0,0,0.12)"
                  border="1px solid"
                  borderColor="gray.100"
                  sx={{ aspectRatio: '16 / 9' }}
                >
                  <Image
                    src={installation}
                    alt="Installation de pompe à chaleur par Groupe Solarenov"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                </Box>
              </GridItem>
              <GridItem>
                <Box
                  bg="rgba(255,255,255,0.10)"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  backdropFilter="blur(8px)"
                  borderRadius="2xl"
                  p={{ base: 5, lg: 7 }}
                >
                <VStack align="start" spacing={4} textAlign="left" color="white">
                  <Heading as="h2" size="xl" fontWeight={800} color="white">
                    Groupe Solarenov, spécialiste de l'énergie solaire en France
                  </Heading>
                  <Text fontSize="lg">
                    Installation optimale avec matériel haut de gamme et domotique, par nos techniciens RGE qualifiés QUALI PV.
                  </Text>
                  <Text>
                    Études personnalisées gratuites, montage du dossier administratif et financier, solutions sur mesure pour maximiser votre énergie solaire.
                  </Text>
                  <Text>
                    Nos équipes certifiées RGE interviennent dans toute la région PACA avec garantie décennale.
                  </Text>
                  <Box pt={2} w="full">
                    <UnderlineCTA
                      text="Découvrez nos réalisations"
                      onClick={() => { window.location.href = '/a-propos'; }}
                      color="white"
                      underlineColor="whiteAlpha.800"
                      align="left"
                    />
                  </Box>
                </VStack>
                </Box>
              </GridItem>
            </Grid>
          </Container>
        </Box>
        <VStack spacing={6} textAlign="center" mb={{ base: 12, lg: 16 }}>
          <Box w="80px" h="8px" bg="linear-gradient(90deg, #094c26, #0ea5e9, #8b5cf6)" borderRadius="full" />
          <Heading as="h2" size="2xl" fontWeight={800} letterSpacing="-0.02em" color="gray.900">
            Nos solutions énergétiques durables
          </Heading>
          <Text fontSize="lg" color="gray.600" maxW="3xl">
            Des installations de qualité pour réduire votre empreinte carbone et vos factures d'énergie
          </Text>
        </VStack>

        {/* Ligne 1 : 2 services */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={{ base: 6, lg: 8 }} mb={8}>
          {servicesData.slice(0, 2).map((service, index) => (
            <GridItem key={index} display="flex">
              <Box
                bg="white"
                borderRadius="2xl"
                boxShadow="0 8px 30px rgba(0,0,0,0.06)"
                border="1px solid"
                borderColor="gray.100"
                transition="all 0.25s ease"
                _hover={{ transform: 'translateY(-6px)', boxShadow: '0 16px 40px rgba(0,0,0,0.10)' }}
                p={6}
                display="flex"
                flexDirection="column"
                h="100%"
                w="100%"
              >
                <VStack align="center" spacing={4} color="gray.900" flex="1" justify="space-between">
                  <Box
                    w="72px"
                    h="72px"
                    bg="white"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="0 8px 20px rgba(0,0,0,0.06)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Image src={service.icon} alt={service.title} w="40px" h="40px" />
                  </Box>
                  <Heading size="md" fontWeight={800} color="gray.900" textAlign="center">
                    {service.title}
                  </Heading>
                  <Text color="gray.600" fontSize="sm" textAlign="center">{service.description}</Text>
                  <VStack align="start" spacing={2}>
                    {service.features.map((feature, i) => (
                      <HStack key={i} spacing={2}>
                        <Text as="span" aria-hidden>•</Text>
                        <Text fontSize="sm"><Text as="b">{feature.split(':')[0]}</Text>{feature.includes(':') ? `:${feature.split(':').slice(1).join(':')}` : ''}</Text>
                      </HStack>
                    ))}
                  </VStack>
                  <HStack pt={2} justify="center" w="100%">
                    <Badge bg="#094c26" color="white" borderRadius="full" px={4} py={2} fontWeight={800}>
                      {service.price}
                    </Badge>
                  </HStack>
                </VStack>
              </Box>
            </GridItem>
          ))}
        </Grid>

        {/* Ligne 2 : 3 services */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={{ base: 6, lg: 8 }} mb={12}>
          {servicesData.slice(2).map((service, idx) => (
            <GridItem key={idx} display="flex">
              <Box
                bg="white"
                borderRadius="2xl"
                boxShadow="0 8px 30px rgba(0,0,0,0.06)"
                border="1px solid"
                borderColor="gray.100"
                transition="all 0.25s ease"
                _hover={{ transform: 'translateY(-6px)', boxShadow: '0 16px 40px rgba(0,0,0,0.10)' }}
                p={6}
                display="flex"
                flexDirection="column"
                h="100%"
                w="100%"
              >
                <VStack align="center" spacing={4} color="gray.900" flex="1" justify="space-between">
                  <Box
                    w="72px"
                    h="72px"
                    bg="white"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="0 8px 20px rgba(0,0,0,0.06)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Image src={service.icon} alt={service.title} w="40px" h="40px" />
                  </Box>
                  <Heading size="md" fontWeight={800} color="gray.900" textAlign="center">
                    {service.title}
                  </Heading>
                  <Text color="gray.600" fontSize="sm" textAlign="center">{service.description}</Text>
                  <VStack align="start" spacing={2}>
                    {service.features.map((feature, i) => (
                      <HStack key={i} spacing={2}>
                        <Text as="span" aria-hidden>•</Text>
                        <Text fontSize="sm"><Text as="b">{feature.split(':')[0]}</Text>{feature.includes(':') ? `:${feature.split(':').slice(1).join(':')}` : ''}</Text>
                      </HStack>
                    ))}
                  </VStack>
                  <HStack pt={2} justify="center" w="100%">
                    <Badge bg="#094c26" color="white" borderRadius="full" px={4} py={2} fontWeight={800}>
                      {service.price}
                    </Badge>
                  </HStack>
                </VStack>
              </Box>
            </GridItem>
          ))}
        </Grid>

        <Box bg="linear-gradient(135deg, #094c26, #0d6b35, #063a1d)" borderRadius="2xl" p={{ base: 8, lg: 12 }} color="white" textAlign="center">
          <VStack spacing={5}>
            <Heading size="lg" fontWeight={800}>Passez à l'énergie solaire avec Groupe Solarenov</Heading>
            <Text opacity={0.95} fontSize="lg">Contactez-nous pour une étude personnalisée gratuite</Text>
            <HStack spacing={4} justify="center" flexWrap="wrap">
              <Button 
                size="lg" 
                bg="white" 
                color="#094c26" 
                _hover={{ bg: 'gray.100' }} 
                fontWeight={700}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Demander un devis gratuit
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                borderColor="white" 
                color="white" 
                _hover={{ bg: 'white', color: '#094c26' }} 
                fontWeight={700}
                onClick={() => { window.location.href = 'tel:0401001118'; }}
              >
                Appelez-nous
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesSection;


